import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './Auth.css';

export function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get redirect path from location state or default to dashboard
  const from = (location.state as { from?: string })?.from || '/dashboard';
  const isRTL = i18n.language === 'fa';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || t('common.error'));
    }

    setIsLoading(false);
  };

  return (
    <div className={`auth-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="auth-card">
          <h1 className="auth-title">{t('auth.loginTitle')}</h1>

          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('waitlist.emailPlaceholder')}
                required
                disabled={isLoading || authLoading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.password')}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoading || authLoading}
                autoComplete="current-password"
                minLength={8}
              />
            </div>

            <div className="form-actions">
              <Link to="/forgot-password" className="forgot-password-link">
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={isLoading || authLoading}
            >
              {isLoading ? t('common.loading') : t('common.login')}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              {t('auth.noAccount')}{' '}
              <Link to="/register">{t('common.register')}</Link>
            </p>
          </div>
        </div>

        <div className="auth-back">
          <Link to="/">← {t('common.back')}</Link>
        </div>
      </div>
    </div>
  );
}
